import {getElementSelector} from '../utils/getElementSelector';
import {fetchPath} from '../modules/fetchState';
import {toast} from '../../../components/kui/toast/toast';
import {handleOnLoads} from './bind';
import {sleep} from '../utils/sleep';
import {findAll} from '../utils/dom/findAll';
import {find} from '../utils/dom/find';
import {isHome} from '../utils/context/isHome';
import {isNewSub} from '../utils/context/isNewSub';
import {isNewContext} from '../utils/context/isNewContext';
import {persistState as persistTreeViewState} from '../../../components/kui/tree_view/tree_view';
import {makeHandler} from '../components/make/main';

const parseJson = (j = '{}') => {
    try {
        if (typeof j !== 'object') {
            return JSON.parse(j || '{}');
        } else {
            return j;
        }
    } catch (error) {
        return {};
    }
};

const setFocus = (nextRoot) => {
    let elementToFocus = nextRoot.querySelector(
        '[autofocus], [data-kui-navigation-custom-focus]'
    );
    const isInsideInertContainer = elementToFocus?.closest('[inert]');
    if (elementToFocus?.hasAttribute('data-kui-navigation-custom-focus')) {
        elementToFocus.setAttribute('tabindex', '-1');
    }

    if (!elementToFocus || isInsideInertContainer) {
        const h1s = nextRoot.querySelectorAll('main h1, [role="dialog"] h1');
        h1s.forEach((item) => {
            item?.setAttribute('tabindex', '-1');
            elementToFocus = item;
        });
    }

    elementToFocus?.focus();
};

const persistShowHides = (newInput, oldInput) => {
    const newRoot = newInput.closest('[data-toggle-display="root"]');
    const newTargets = newRoot.querySelectorAll(
        '[data-toggle-display="target"]'
    );
    const oldRoot = oldInput.closest('[data-toggle-display="root"]');
    const oldTargets = oldRoot.querySelectorAll(
        '[data-toggle-display="target"]'
    );

    [...newTargets].forEach((target, i) => {
        target.hidden = oldTargets[i].hidden;
    });
};

const persistChecked = (oldInput, newInput) => {
    const oldChecked = oldInput.checked;
    newInput.checked = oldChecked;
};

const persistValue = (oldInput, newInput) => {
    const oldValue = oldInput.value;
    newInput.value = oldValue;
};

const persistInputs = (nextRoot) => {
    [...findAll('[kui-input-persist]', nextRoot)].forEach((newInput) => {
        const isRadioCheckbox = ['radio', 'checkbox'].includes(newInput.type);
        const isConditionalField = newInput.hasAttribute(
            'data-conditional-field'
        );
        const oldInput = isRadioCheckbox
            ? find(
                  `[name='${newInput.getAttribute(
                      'name'
                  )}'][value='${newInput.getAttribute('value')}']`
              )
            : isConditionalField
              ? find(`[name='${newInput.getAttribute('name')}']`)
              : find(
                    getElementSelector(newInput, {
                        root: nextRoot
                    })
                );

        if (oldInput) {
            const updateFunction = isRadioCheckbox
                ? persistChecked
                : persistValue;
            updateFunction(oldInput, newInput);
            if (
                isRadioCheckbox &&
                newInput.getAttribute('kui-on-change') &&
                newInput
                    .getAttribute('kui-on-change')
                    .startsWith('toggleDisplay.')
            ) {
                persistShowHides(newInput, oldInput);
            }
        }
    });
};

const replaceDom = (
    prevRoot,
    nextRoot,
    isFromHome,
    isToHome,
    isChangeSub,
    isChangeContext,
    isModalContext
) => {
    if (isChangeSub && !isModalContext) {
        nextRoot.classList.add('has-page-transition');
    }

    persistInputs(nextRoot);
    persistTreeViewState(prevRoot, nextRoot);

    const oldPageTitleEl = prevRoot.querySelector('[data-page-title]');
    const oldPageTitle = oldPageTitleEl.getAttribute('data-page-title');

    prevRoot.replaceWith(nextRoot);

    const pageTitleEl = nextRoot.querySelector('[data-page-title]');
    if (pageTitleEl) {
        document.title = pageTitleEl.getAttribute('data-page-title');
    }

    if (
        // If a modal URL, we don't want to set focus on the H1
        !isModalContext &&
        (document.title !== oldPageTitle || isChangeContext)
    ) {
        setFocus(nextRoot);
    }

    handleOnLoads();

    return nextRoot;
};

const navigate = (res, oldContext, newContext) => {
    const parser = new DOMParser();
    const dom = parser.parseFromString(res.html, 'text/html');
    // Default to roast root, use Kinde root for custom UI
    // as roast wouldn't make sense in that context
    // fall back to body so custom ui doesn't break if omitted
    let ROOT_SELECTOR = '[data-roast-root]';
    let prevRoot = document.querySelector(ROOT_SELECTOR);
    if (!prevRoot) {
        ROOT_SELECTOR = '[data-kinde-root]';
        prevRoot = document.querySelector(ROOT_SELECTOR);
    }
    if (!prevRoot) {
        ROOT_SELECTOR = 'body';
        prevRoot = document.body;
    }
    const nextRoot = find(ROOT_SELECTOR, dom);

    const metaCsrf = dom.querySelector('meta[name="csrf-token"]');
    const currentMetaCsrf = document.querySelector('meta[name="csrf-token"]');
    if (metaCsrf && currentMetaCsrf) {
        currentMetaCsrf.content = metaCsrf.content;
    }

    const isFromHome = isHome(oldContext);
    const isToHome = isHome(newContext);
    const isModalContext = newContext && 'is_modal' in newContext;

    (async () => {
        await sleep();
    })();

    if (nextRoot) {
        replaceDom(
            prevRoot,
            nextRoot,
            isFromHome,
            isToHome,
            isNewSub(oldContext, newContext),
            isNewContext(oldContext, newContext),
            isModalContext
        );
    }

    return nextRoot;
};

const handleRedirect = (res) => (window.location = res.location);

const handleRefresh = () => fetchPath(history.state, false);

const handleNoop = () => false;

const handlePolling = () => false;

const handleToast = (res) => toast(res);

const handleMake = (res) => makeHandler(res);

const handleAction = (action) => {
    const actionMap = {
        show_toast: handleToast,
        noop: handleNoop,
        polling: handlePolling,
        redirect: handleRedirect,
        refresh_content: handleRefresh,
        make: handleMake
    };
    return actionMap[action] ? actionMap[action] : actionMap.noop;
};

const handleMixed = async (response, oldContext, newContext) => {
    try {
        const res = await response.json();
        const json = parseJson(res.json);
        const html = parseJson(res.html);

        if (json && (json.action || json.actions)) {
            const actions = json.actions || [json];
            actions.forEach((item) => handleAction(item.action)(item));
        }

        if (json && json.action === 'noop') return 'noop';

        if (res.html && !html.actions)
            return navigate(res, oldContext, newContext);

        if (json?.actions?.[0]?.action === 'polling')
            return {action: 'polling', ...json.actions[0]};
    } catch (err) {
        console.error(err);
    }
};

const handleUnknownResponseType = (response) => {
    console.error(
        `Unknown response content-type. Received: ${response.headers.get(
            'content-type'
        )}. Expected: roast/mixed`
    );
};

const handleFileToLarge = () => {
    throw 'Your uploaded file is too large, check the accepted size';
};

const getHandleCode = (code) => {
    const map = {
        413: handleFileToLarge,
        default: handleUnknownResponseType
    };
    return map[code] ? map[code] : map.default;
};

const handleTextHTML = (response) => {
    const handleCode = getHandleCode(response.status);
    handleCode(response);
};

const handleContentType = (contentType) => {
    const map = {
        'text/html': handleTextHTML,
        'roast/mixed': handleMixed,
        default: handleUnknownResponseType
    };
    return map[contentType] ? map[contentType] : map.default;
};

const handleResponse = async (response, newContext, oldContext) => {
    const internalRedirectTo = response.headers.get('x-intra-redirect');
    const contentType = response.headers.get('content-type');

    if (
        internalRedirectTo &&
        !history.state.path.endsWith(internalRedirectTo)
    ) {
        // Make back button skip pages it was redirected from (e.g. modal)
        history.pushState({path: internalRedirectTo}, '', internalRedirectTo);
    }

    const func = handleContentType(contentType);
    return await func(response, oldContext, newContext);
};

export {handleResponse};
